/**
 * Derive KOT point windows from pose heuristics (serve → last stroke recovery).
 * No video decode — runs on stored pose result_json.
 */

import { buildStrokeClusters } from "./poseHeuristics";

const STROKE_DETECTION_IDS = [
  "fh_left",
  "fh_topspin_left",
  "bh_left",
  "bh_topspin_left",
];

const DEFAULT_OPTS = Object.freeze({
  prepPadMs: 400,
  postPadMs: 800,
  minDurationMs: 1200,
  mergeGapMs: 1500,
  idleGapMs: 3000,
  minServeSpacingMs: 2200,
});

function num(v, fallback = null) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function mergeCloseWindows(windows, mergeGapMs) {
  if (!windows.length) return [];
  const sorted = [...windows].sort((a, b) => a.start_ms - b.start_ms);
  const out = [{ ...sorted[0] }];
  for (let i = 1; i < sorted.length; i += 1) {
    const prev = out[out.length - 1];
    const cur = sorted[i];
    if (cur.start_ms - prev.end_ms <= mergeGapMs) {
      prev.end_ms = Math.max(prev.end_ms, cur.end_ms);
      prev.stroke_hint_count = Math.max(
        prev.stroke_hint_count || 0,
        cur.stroke_hint_count || 0
      );
    } else {
      out.push({ ...cur });
    }
  }
  return out;
}

function strokeContactMs(stroke) {
  return (
    num(stroke?.t_contact_ms) ??
    num(stroke?.t_peak_ms) ??
    num(stroke?.t_valley_ms)
  );
}

function strokePrepStartMs(stroke) {
  const prep = stroke?.phases?.preparation?.t_start_ms;
  if (num(prep) != null) return num(prep);
  const contact = strokeContactMs(stroke);
  return contact != null ? contact - 250 : null;
}

function strokeRecoveryEndMs(stroke) {
  const rec = stroke?.phases?.recovery?.t_end_ms ?? stroke?.phases?.recovery?.t_start_ms;
  if (num(rec) != null) return num(rec);
  const peak = num(stroke?.t_peak_ms) ?? strokeContactMs(stroke);
  return peak != null ? peak + 450 : null;
}

function serveEventStartMs(event) {
  const prep = event?.phases?.preparation?.t_start_ms ?? event?.t_prep_start_ms;
  if (num(prep) != null) return num(prep);
  const t = num(event?.t_ms) ?? num(event?.t_contact_ms) ?? num(event?.t_peak_ms);
  return t != null ? t - 300 : null;
}

function serveEventMs(event) {
  return (
    num(event?.t_ms) ??
    num(event?.t_contact_ms) ??
    num(event?.t_peak_ms) ??
    serveEventStartMs(event)
  );
}

/** Union strokes from all FH/BH detections (both players in KoT). */
export function collectPoseStrokes(poseResult) {
  const detections = poseResult?.summary?.detections || {};
  const byContact = new Map();
  for (const id of STROKE_DETECTION_IDS) {
    const strokes = detections[id]?.strokes;
    if (!Array.isArray(strokes)) continue;
    for (const stroke of strokes) {
      const contact = strokeContactMs(stroke);
      if (contact == null) continue;
      const key = String(Math.round(contact));
      if (!byContact.has(key)) {
        byContact.set(key, { ...stroke, _contact_ms: contact });
      }
    }
  }
  return [...byContact.values()].sort(
    (a, b) => a._contact_ms - b._contact_ms
  );
}

export function collectServeEvents(poseResult) {
  const events = poseResult?.summary?.detections?.serve_toss?.events;
  if (!Array.isArray(events)) return [];
  return events
    .map((ev) => ({
      ...ev,
      _t_ms: serveEventMs(ev),
      _start_ms: serveEventStartMs(ev),
    }))
    .filter((ev) => ev._t_ms != null)
    .sort((a, b) => a._t_ms - b._t_ms);
}

function strokesInWindow(strokes, startMs, endMs) {
  return strokes.filter((s) => {
    const c = s._contact_ms ?? strokeContactMs(s);
    return c != null && c >= startMs && c < endMs;
  });
}

function rawWindowsFromServes(serves, strokes, durationMs) {
  if (!serves.length) return [];
  const windows = [];
  for (let i = 0; i < serves.length; i += 1) {
    const serve = serves[i];
    const nextServe = serves[i + 1];
    const startMs = serve._start_ms ?? serve._t_ms;
    const endBound = nextServe ? nextServe._t_ms : durationMs;
    const inWin = strokesInWindow(strokes, startMs, endBound);
    let endMs = endBound;
    if (inWin.length) {
      const last = inWin[inWin.length - 1];
      endMs = strokeRecoveryEndMs(last) ?? endBound;
    } else if (nextServe) {
      endMs = nextServe._start_ms ?? nextServe._t_ms;
    }
    windows.push({
      start_ms: startMs,
      end_ms: endMs,
      stroke_hint_count: inWin.length || null,
    });
  }
  return windows;
}

function rawWindowsFromStrokeClusters(strokes, idleGapMs) {
  if (!strokes.length) return [];
  const clusters = buildStrokeClusters(
    strokes.map((s) => ({
      ...s,
      t_peak_ms: s._contact_ms ?? strokeContactMs(s),
    })),
    idleGapMs
  );
  return clusters.map((cluster) => {
    const first = cluster[0];
    const last = cluster[cluster.length - 1];
    return {
      start_ms: strokePrepStartMs(first) ?? strokeContactMs(first),
      end_ms: strokeRecoveryEndMs(last) ?? strokeContactMs(last),
      stroke_hint_count: cluster.length,
    };
  });
}

function finalizeSegments(rawWindows, durationMs, opts) {
  const o = { ...DEFAULT_OPTS, ...opts };
  const maxEnd = num(durationMs) ?? 0;
  if (!rawWindows.length) return [];

  const padded = rawWindows.map((w) => ({
    start_ms: Math.max(0, w.start_ms - o.prepPadMs),
    end_ms: Math.min(maxEnd + o.postPadMs, w.end_ms + o.postPadMs),
    stroke_hint_count: w.stroke_hint_count ?? null,
  }));

  const merged = mergeCloseWindows(padded, o.mergeGapMs).filter(
    (w) => w.end_ms - w.start_ms >= o.minDurationMs
  );

  return merged.map((w, index) => ({
    index: index + 1,
    start_ms: Math.round(w.start_ms),
    end_ms: Math.round(w.end_ms),
    duration_ms: Math.round(w.end_ms - w.start_ms),
    stroke_hint_count: w.stroke_hint_count,
    source: "pose_heuristics",
  }));
}

/**
 * @param {object} poseResult
 * @param {number|null} durationSec
 * @param {object} [opts]
 */
export function pointSegmentsFromPoseResult(poseResult, durationSec = null, opts = {}) {
  if (!poseResult || typeof poseResult !== "object") return [];

  const durationMs =
    durationSec != null && Number.isFinite(Number(durationSec))
      ? Math.round(Number(durationSec) * 1000)
      : (() => {
          const samples = poseResult.samples;
          if (Array.isArray(samples) && samples.length) {
            return num(samples[samples.length - 1]?.t_ms, 0);
          }
          return 0;
        })();

  const strokes = collectPoseStrokes(poseResult);
  const serves = collectServeEvents(poseResult);
  if (!strokes.length && !serves.length) return [];

  let raw = [];
  if (serves.length) {
    raw = rawWindowsFromServes(serves, strokes, durationMs || Infinity);
  }
  if (!raw.length && strokes.length) {
    raw = rawWindowsFromStrokeClusters(strokes, opts.idleGapMs ?? DEFAULT_OPTS.idleGapMs);
  }
  return finalizeSegments(raw, durationMs, opts);
}

export function resolvePointSegmentsFromPoseResult(poseResult, durationSec = null) {
  if (!poseResult || typeof poseResult !== "object") return [];
  const stored = poseResult.pose_point_segments || poseResult.point_segments;
  if (Array.isArray(stored) && stored.length && stored[0]?.source === "pose_heuristics") {
    return stored
      .map((s, i) => ({
        index: num(s.index, i + 1),
        start_ms: num(s.start_ms, 0),
        end_ms: num(s.end_ms, 0),
        duration_ms: num(s.duration_ms, num(s.end_ms, 0) - num(s.start_ms, 0)),
        stroke_hint_count: s.stroke_hint_count ?? null,
        source: "pose_heuristics",
      }))
      .filter((s) => s.end_ms > s.start_ms);
  }
  return pointSegmentsFromPoseResult(poseResult, durationSec);
}
